import { useEffect, useState } from 'react';

import { Button, message } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import AsyncBulkUpload from './AsyncBulkUpload';
import {
  JOB_NAMES,
  JOB_TYPES,
  POLLING_FREQ,
  TYPE_TITLE_MAP,
  UI_JOB_STATUS,
  JOB_STATUS,
  JOB_STATUS_MAPPING,
  getCTAText,
  ifButtonDisabled,
  getJobType,
  SO_TYPE_TITLE_MAP,
  SO_JOB_NAMES,
  SO_JOB_STATUS_MAPPING
} from './BulkUploaderUtils';
import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError, validator, delay } from '../../clientUtils';
import { BULK_UPLOAD_INVOICE_TYPES, FILE_TYPE_ATTRIBUTE } from '../../constants';
import SOInfoMessage from './SOInfoMessage';

/**
    BULK_RO_UPDATE_SEND_FOR_APPROVAL,
    BULK_RO_UPDATE_APPROVE,
    BULK_RO_UPDATE_REJECT,
    BULK_RO_UPDATE_OPEN,
    BULK_RO_UPDATE_DISPATCH
 */

const onError = (error) => message.error(getError(error));

const onUpload = (jobName, file, onSuccess) => {
  CallApi(apiConfig.job.uploadFile, { job_name: jobName }, { file }, undefined, (res) => onSuccess(file, res), onError);
};
/**
 *
 * @param {string} type type of upload
 * @param {string} jobType [validation | submit]
 * @param {url} fileLink s3 link
 * @returns {object}
 */
const getBody = (type, jobType, fileLink, validationJobId) => {
  const fileType = `BULK_SO_${SO_JOB_NAMES[type]}_${jobType === JOB_TYPES.validation ? 'VALIDATION_FILE' : 'SUBMIT_FILE'}`;
  const baseJobName = 'POSI_SO';

  if (jobType === JOB_TYPES.validation) {
    return {
      job_name: `${baseJobName}_${SO_JOB_NAMES[type]}_VALIDATE`,
      files: [
        {
          attachment_type: fileType,
          file_url: fileLink
        }
      ]
    };
  }
  return {
    job_name: `${baseJobName}_${SO_JOB_NAMES[type]}_SUBMIT`,
    files: [
      {
        attachment_type: fileType,
        file_url: fileLink
      }
    ]
  };
};

const AsyncSOUploader = () => {
  const { type, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [sampleDownloadLink, setSampleDownloadLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [buttonLoading, setButtonLoading] = useState(false);

  const navigate = useNavigate();

  // fetching sample file
  useEffect(() => {
    setFiles([]);
    setJobStatus(UI_JOB_STATUS.uploading);
    const typeMap = {
      dispatch: 'soDispatch',
      deliver: 'soDeliver'
    };
    CallApi(
      apiConfig.bulkUploader[typeMap[type]].sample,
      { invoice_type: BULK_UPLOAD_INVOICE_TYPES.DOMESTIC_ONLINE },
      null,
      setLoading,
      (res) => setSampleDownloadLink(res),
      () => message.error('Unable to download sample file!')
    );
  }, [type]);

  // if id is there then fetching the data
  useEffect(() => {
    let pollingId;
    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.details, path: apiConfig.job.details.path(id) },
          null,
          null,
          null,
          (res) => {
            // jobtype == submit and status == success
            if (
              (res.job_name.includes('SUBMIT') && res?.job_status === JOB_STATUS.success) ||
              res?.job_status === JOB_STATUS.submitted
            ) {
              message.info('File submitted, redirecting to listing page');
              delay(3000, () => navigate('/sales/ro'), onError);
            }
            if (SO_JOB_STATUS_MAPPING[res?.job_status] === 'end' && pollingId) {
              clearInterval(pollingId);
            }

            const file = res?.response?.files?.find(
              (f) =>
                (f.attachment_type.includes(JOB_NAMES[type]) && f.attachment_type.includes('VALID')) ||
                (f.attachment_type.includes(JOB_NAMES[type]) && f.attachment_type.includes('SUBMIT'))
            );
            setFiles([
              {
                ...file,
                summary: file?.validation_summary,
                link: file?.file_url,
                fileLink: file?.file_url,
                message: file?.failure_message
              }
            ]);
            setJobStatus(getJobType(res?.job_status, res.job_name, [file?.validation_summary]));
          },
          onError
        );
      };

      getJobStatus();

      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }
    return () => clearInterval(pollingId);
  }, [navigate, type, id]);

  const uploadProps = {
    title: `SO ${type}`,
    description: 'Please upload the list of SKUs (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files,
    validator,
    onUpload: (file) =>
      onUpload(`POSI_SO_${type?.toUpperCase()}_VALIDATE`, file, (f, link) => {
        setFiles([{ ...f, fileLink: link }]);
        setJobStatus(UI_JOB_STATUS.uploaded);
      }),
    onRemove: async () => {
      setFiles([]);
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: [UI_JOB_STATUS.submitted, UI_JOB_STATUS.submitting, UI_JOB_STATUS.validating].includes(jobStatus)
  };

  const fileUploaderProps = {
    message: '',
    items: [uploadProps]
  };

  const handleClick = () => {
    const typeMap = {
      dispatch: 'soDispatch',
      deliver: 'soDeliver'
    };
    // jobStatus === uploaded -> validation job
    if (jobStatus === UI_JOB_STATUS.uploaded) {
      CallApi(
        apiConfig.bulkUploader[typeMap[type]].validate,
        null,
        getBody(type, JOB_TYPES.validation, files?.[0]?.fileLink),
        setButtonLoading,
        (res) => {
          navigate(`/bulkuploader/so/${type}/${res.id}`);
        },
        onError
      );
      return;
    }
    // jobStatus === validated -> submission job
    CallApi(
      apiConfig.bulkUploader[typeMap[type]].submit,
      null,
      getBody(type, JOB_TYPES.submit, files?.[0]?.fileLink, id),
      setButtonLoading,
      (res) => {
        message.success('Job submitted');
        delay(2000);
        navigate(`/bulkuploader/so/${type}/${res.id}`);
      },
      onError
    );
  };

  return (
    <AsyncBulkUpload
      button={
        <Button
          disabled={ifButtonDisabled(jobStatus, files?.validationSummary)}
          loading={buttonLoading}
          onClick={handleClick}
          type="primary">
          {getCTAText(jobStatus)}
        </Button>
      }
      info={<SOInfoMessage type={type} status={jobStatus} />}
      loading={loading}
      menuKey={`so-${type}-bulk`}
      title={SO_TYPE_TITLE_MAP[type]}
      uploadersProp={[fileUploaderProps]}
    />
  );
};

export default AsyncSOUploader;
